import React, { useState, useEffect } from 'react'
import { API_ENDPOINTS } from '../../../config/api'
import apiClient from '../../../config/api'
import LaybyPayment from './LaybyPayment'
import Swal from 'sweetalert2'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

const LaybyDetail = ({ layby, userInfo, branchId, onBack, onPaymentSuccess }) => {
  const [laybyDetails, setLaybyDetails] = useState(layby)
  const [loading, setLoading] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    // Fetch fresh details when component mounts
    fetchLaybyDetails()
  }, [layby.id])

  const fetchLaybyDetails = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.LAYBY}${layby.id}/`)
      setLaybyDetails(response.data)
    } catch (error) {
      console.error('Error fetching layby details:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return `$${Number(value).toFixed(2)}`
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-primary', text: 'Active' },
      completed: { bg: 'bg-success', text: 'Completed' },
      cancelled: { bg: 'bg-danger', text: 'Cancelled' },
      default: { bg: 'bg-secondary', text: status || 'Unknown' },
    }

    const config = statusConfig[status?.toLowerCase()] || statusConfig.default

    return (
      <span className={`badge ${config.bg} text-white px-3 py-2`}>
        {config.text}
      </span>
    )
  }

  const isOverdue = (dueDate, status) => {
    if (status === 'completed') return false
    const due = new Date(dueDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return due < today
  }

  const handleCancel = async () => {
    const confirmationWord = 'CANCEL'
    
    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      if (!text) return ''
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }
      return text.replace(/[&<>"']/g, m => map[m])
    }
    
    const escapedWord = escapeHtml(confirmationWord)
    
    const { value: typedWord } = await Swal.fire({
      title: 'Cancel Layby',
      html: `
        <p>Are you sure you want to cancel this layby?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedWord}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type ${confirmationWord}">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Cancel Layby',
      cancelButtonText: 'Keep Layby',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()
        
        // Initially disable the confirm button
        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'
        
        // Add input event listener to enable/disable button based on match
        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === confirmationWord
          
          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })
        
        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input?.value.trim()
        
        if (inputValue !== confirmationWord) {
          Swal.showValidationMessage(`Please type "${confirmationWord}" to confirm`)
          return false
        }
        
        return inputValue
      }
    })

    // If user confirmed (typed the word correctly)
    if (typedWord === confirmationWord) {
      const laybyToCancel = laybyDetails || layby
      setLoading(true)
      try {
        await apiClient.post(`${API_ENDPOINTS.LAYBY}${laybyToCancel.id}/cancel/`)
        
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Layby cancelled successfully',
          confirmButtonColor: brandColors.primary,
        })
        
        // Refresh layby details
        await fetchLaybyDetails()
        
        // Notify parent component if callback exists
        if (onPaymentSuccess) {
          onPaymentSuccess()
        }
      } catch (error) {
        console.error('Error cancelling layby:', error)
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Failed to cancel layby. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: brandColors.primary,
        })
      } finally {
        setLoading(false)
      }
    }
  }

  if (loading && !laybyDetails) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  const currentLayby = laybyDetails || layby
  const canMakePayment = currentLayby.status !== 'completed' && Number(currentLayby.balance) > 0
  const canCancel = currentLayby.status !== 'completed' && currentLayby.status !== 'cancelled'

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <button
            className="btn btn-outline-secondary btn-sm mb-3"
            onClick={onBack}
          >
            <i className="bi bi-arrow-left me-2" />
            Back to List
          </button>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h4 className="mb-0">{currentLayby.layby_number}</h4>
            {getStatusBadge(currentLayby.status)}
            {isOverdue(currentLayby.due_date, currentLayby.status) && (
              <span className="badge bg-danger">Overdue</span>
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          {canCancel && (
            <button
              className="btn btn-danger"
              style={{
                border: 'none',
              }}
              onClick={handleCancel}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-2" />
              Cancel Layby
            </button>
          )}
          {canMakePayment && (
            <button
              className="btn text-white"
              style={{
                background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                border: 'none',
              }}
              onClick={() => setShowPaymentModal(true)}
            >
              <i className="bi bi-cash-coin me-2" />
              Make Payment
            </button>
          )}
        </div>
      </div>

      <div className="row">
        {/* Left Column - Main Details */}
        <div className="col-lg-8">
          {/* Customer Information */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-person me-2" />
                Customer Information
              </h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <p className="text-muted small mb-1">Customer Name</p>
                  <p className="fw-semibold mb-0">{currentLayby.customer_name}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <p className="text-muted small mb-1">Phone Number</p>
                  <p className="fw-semibold mb-0">{currentLayby.customer_phone}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <p className="text-muted small mb-1">Branch</p>
                  <p className="fw-semibold mb-0">{currentLayby.branch_name || 'N/A'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <p className="text-muted small mb-1">Due Date</p>
                  <p className={`fw-semibold mb-0 ${isOverdue(currentLayby.due_date, currentLayby.status) ? 'text-danger' : ''}`}>
                    {formatDate(currentLayby.due_date)}
                    {isOverdue(currentLayby.due_date, currentLayby.status) && (
                      <span className="badge bg-danger ms-2">Overdue</span>
                    )}
                  </p>
                </div>
                {currentLayby.notes && (
                  <div className="col-12">
                    <p className="text-muted small mb-1">Notes</p>
                    <p className="mb-0">{currentLayby.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-bag me-2" />
                Items ({currentLayby.items?.length || 0})
              </h5>
              {currentLayby.items && currentLayby.items.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentLayby.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.product_name}</td>
                          <td>
                            <span className="badge bg-secondary">{item.product_sku}</span>
                          </td>
                          <td className="text-end">{Number(item.quantity).toFixed(0)}</td>
                          <td className="text-end">{formatCurrency(item.unit_price)}</td>
                          <td className="text-end fw-semibold">
                            {formatCurrency(item.subtotal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bold">
                        <td colSpan="4" className="text-end">
                          Total Amount:
                        </td>
                        <td className="text-end">{formatCurrency(currentLayby.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No items found</p>
              )}
            </div>
          </div>

          {/* Payment History */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="bi bi-clock-history me-2" />
                Payment History ({currentLayby.payments?.length || 0})
              </h5>
              {currentLayby.payments && currentLayby.payments.length > 0 ? (
                <div className="list-group list-group-flush">
                  {currentLayby.payments
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((payment) => (
                      <div
                        key={payment.id}
                        className="list-group-item px-0 py-3 border-bottom"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className="fw-bold">{formatCurrency(payment.amount)}</span>
                              <span className="badge bg-primary">
                                {payment.payment_method_display || payment.payment_method}
                              </span>
                            </div>
                            {payment.notes && (
                              <p className="text-muted small mb-1">{payment.notes}</p>
                            )}
                            <p className="text-muted small mb-0">
                              <i className="bi bi-clock me-1" />
                              {formatDateTime(payment.created_at)}
                            </p>
                            <p className="text-muted small mb-0">
                              <i className="bi bi-person me-1" />
                              {payment.cashier_email || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-muted">No payments recorded yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
            <div className="card-body">
              <h6 className="card-title mb-4">Payment Summary</h6>

              <div className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Total Amount:</span>
                  <span className="fw-bold">{formatCurrency(currentLayby.total_amount)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Deposit:</span>
                  <span>{formatCurrency(currentLayby.deposit)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Total Paid:</span>
                  <span className="text-success fw-semibold">
                    {formatCurrency(currentLayby.total_paid)}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">Remaining Balance:</span>
                  <span
                    className={`fw-bold fs-4 ${
                      Number(currentLayby.balance) > 0 ? 'text-warning' : 'text-success'
                    }`}
                  >
                    {formatCurrency(currentLayby.balance)}
                  </span>
                </div>
              </div>

              <div className="border-top pt-3">
                <p className="text-muted small mb-2">Created:</p>
                <p className="small mb-3">{formatDateTime(currentLayby.created_at)}</p>
                <p className="text-muted small mb-2">Last Updated:</p>
                <p className="small mb-0">{formatDateTime(currentLayby.updated_at)}</p>
              </div>

              {canCancel && (
                <button
                  className="btn btn-danger w-100 mt-3"
                  style={{
                    border: 'none',
                  }}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="bi bi-x-circle me-2" />
                  Cancel Layby
                </button>
              )}
              {canMakePayment && (
                <button
                  className="btn w-100 mt-4 text-white"
                  style={{
                    background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                    border: 'none',
                  }}
                  onClick={() => setShowPaymentModal(true)}
                >
                  <i className="bi bi-cash-coin me-2" />
                  Make Payment
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <LaybyPayment
          layby={currentLayby}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false)
            fetchLaybyDetails()
            if (onPaymentSuccess) {
              onPaymentSuccess()
            }
          }}
        />
      )}
    </div>
  )
}

export default LaybyDetail
